/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useState } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import BugIcon from '@atlaskit/icon/core/bug';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import twgLogo from '../../../../assets/twg-logo.svg';
import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { usePanelContext } from '../../../../hooks/use-panel-context';
import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { CollectFeedbackV2 } from '../../CollectFeedback';
import { SiteSwitcher } from '../../SiteSwitcher';
import { NAV_BAR_HEIGHT_PX, UNAUTHENTICATED_FEEDBACK_FORM_URL } from '../constants';
import { messages } from '../messages';
import { UserAvatarDropdown } from '../UserAvatarDropdown';

import { NavButtons } from './NavButtons';

const styles = cssMap({
	navBar: {
		width: '100%',
		backgroundColor: '#111213',
		position: 'fixed',
		top: 0,
		zIndex: 10,
	},
	navBarContent: {
		width: '100%',
		maxWidth: '1264px',
		height: `${NAV_BAR_HEIGHT_PX}px`,
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		paddingInline: token('space.400'),
		marginInline: 'auto',
	},
	logoContainer: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
		justifySelf: 'start',
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
	},
	logo: {
		width: '16px',
		height: '16px',
		'@media (max-width: 30rem)': {
			width: '32px',
			height: '32px',
		},
	},
	logoLabel: {
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
	rightControls: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.200'),
		justifySelf: 'end',
	},
	loginButton: {
		display: 'inline-flex',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '10.5px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '6.5px',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderWidth: '1.5px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#505258',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		cursor: 'pointer',
		transition: 'all 150ms ease-out',
		zIndex: 1,
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	feedbackButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: 'inherit',
		font: 'inherit',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
});

const navBarContentStyles = css({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

const leftNavStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.400'),
});

/**
 * @deprecated remove on FG twg-microsite-enable-standalone-cli-page clean up
 */
export const NavigationBarOld = (): JSX.Element => {
	const { identity: user, loading, error, attemptUserLogin, isAuthed } = useAtlassianUserContext();
	const { formatMessage } = useIntl();
	const { togglePanel, isOpen } = usePanelContext();
	const { fireEvent } = useAnalyticsEvents();
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

	const handleLoginClick = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeLogin', { location: 'navBar' });
		attemptUserLogin();
	}, [fireEvent, attemptUserLogin]);

	const handleFeedbackOpen = useCallback((): void => {
		if (!isAuthed) {
			globalThis.window.open(UNAUTHENTICATED_FEEDBACK_FORM_URL, '_blank');
			return;
		}

		setIsFeedbackOpen(true);
	}, [isAuthed]);
	const handleFeedbackClose = useCallback((): void => {
		setIsFeedbackOpen(false);
	}, []);

	const isLoggedIn = !loading && !error && !!user.email;

	return (
		<UFOSegment name="twg-microsite.nav-bar">
			<div css={styles.navBar}>
				<Box xcss={styles.navBarContent}>
					<div css={navBarContentStyles}>
						<div css={leftNavStyles}>
							<div css={styles.logoContainer}>
								<img src={twgLogo} alt={formatMessage(messages.logoAlt)} css={styles.logo} />
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<span css={styles.logoLabel}>
									<FormattedMessage {...messages.teamworkGraphBrand} />
								</span>
							</div>
							{!loading && (
								<NavButtons
									ariaLabel={formatMessage(messages.teamworkGraphBrand)}
									isLoggedIn={isLoggedIn}
								/>
							)}
						</div>

						<div css={styles.rightControls}>
							{/* TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback */}
							{loading && (
								// `data-vc-ignore-if-no-layout-shift="true"` excludes the
								// auth-loading spinner from React UFO's VC tracking. The
								// `@atlaskit/spinner` SVG runs an infinite CSS animation in
								// a fixed-size box (no layout shift) so VC can safely
								// ignore its mutations — otherwise the spinner kicks the
								// VC quiet window for the full duration of auth resolution
								// and prevents the page-load interaction from sealing.
								<span data-vc-ignore-if-no-layout-shift="true">
									<Spinner size="small" interactionName="twg-microsite.nav-bar.user-loading" />
								</span>
							)}

							{!loading && (
								<Fragment>
									<button type="button" css={styles.feedbackButton} onClick={handleFeedbackOpen}>
										<FormattedMessage {...messages.sendFeedback} />
									</button>
									<CollectFeedbackV2
										atlassianAccountId={user.accountId}
										name={user.name}
										isOpen={isFeedbackOpen}
										onClose={handleFeedbackClose}
									/>
								</Fragment>
							)}

							{error && (
								<button
									css={styles.loginButton}
									onClick={handleLoginClick}
									aria-label={formatMessage(messages.loginAriaLabel)}
								>
									<FormattedMessage {...messages.loginButton} />
								</button>
							)}

							{!loading && !error && user && <SiteSwitcher />}
							{!loading && !error && user && <UserAvatarDropdown user={user} />}

							{fg('twg_microsite_debug_panel') && (
								<IconButton
									icon={BugIcon}
									label="Debug Menu Toggle"
									appearance={isOpen ? 'discovery' : 'default'}
									onClick={togglePanel}
								/>
							)}
						</div>
					</div>
				</Box>
			</div>
		</UFOSegment>
	);
};
